import React, { useState } from 'react';
import { Share2, Linkedin, Mail, Copy, Check } from 'lucide-react';

interface SocialShareProps {
  title: string;
  url: string;
  excerpt: string;
  className?: string;
}

export const SocialShare: React.FC<SocialShareProps> = ({ 
  title, 
  url, 
  excerpt, 
  className = '' 
}) => {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const shareUrl = `${window.location.origin}${url}`;
  
  const shareData = {
    linkedin: {
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      label: 'Share on LinkedIn'
    },
    email: {
      subject: encodeURIComponent(`Check out this article: ${title}`),
      body: encodeURIComponent(`I thought you might find this article interesting:\n\n${title}\n\n${excerpt}\n\nRead more: ${shareUrl}`),
      url: `mailto:?subject=${encodeURIComponent(`Check out this article: ${title}`)}&body=${encodeURIComponent(`I thought you might find this article interesting:\n\n${title}\n\n${excerpt}\n\nRead more: ${shareUrl}`)}`,
      label: 'Share via Email'
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleShare = (platform: 'linkedin' | 'email') => {
    if (platform === 'email') {
      window.location.href = shareData[platform].url;
    } else {
      window.open(shareData[platform].url, '_blank', 'width=600,height=400');
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        aria-label="Share article"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Share2 className="h-4 w-4" aria-hidden="true" />
        <span>Share</span>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Share Menu */}
          <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50" role="menu" aria-orientation="vertical">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Share this article</h3>
              
              <div className="space-y-2">
                {/* LinkedIn Share */}
                <button
                  onClick={() => handleShare('linkedin')}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  aria-label={shareData.linkedin.label}
                  role="menuitem"
                >
                  <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                    <Linkedin className="h-4 w-4 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">LinkedIn</div>
                    <div className="text-xs text-gray-500">Share with your network</div>
                  </div>
                </button>

                {/* Email Share */}
                <button
                  onClick={() => handleShare('email')}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  aria-label={shareData.email.label}
                  role="menuitem"
                >
                  <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center">
                    <Mail className="h-4 w-4 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">Email</div>
                    <div className="text-xs text-gray-500">Send to a colleague</div>
                  </div>
                </button>

                {/* Copy Link */}
                <button
                  onClick={copyToClipboard}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  aria-label="Copy link to clipboard"
                  role="menuitem"
                >
                  <div className="w-8 h-8 bg-gray-400 rounded flex items-center justify-center">
                    {copied ? (
                      <Check className="h-4 w-4 text-white" aria-hidden="true" />
                    ) : (
                      <Copy className="h-4 w-4 text-white" aria-hidden="true" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {copied ? 'Copied!' : 'Copy Link'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {copied ? 'Link copied to clipboard' : 'Copy URL to clipboard'}
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};